/**
 * Pricing loop v2 — Phase 2 remainder: decision cells + candidate generator +
 * experiment memory (plan §2/§3, AC-057..067). PURE module: no React, no IO.
 *
 * Architecture (pinned by the acceptance doc): the FIRST-ITEM row-seed
 * machinery IS the data plane — a cell's seeds are the UNCHANGED
 * `rowServedPrefill` output for its campaign row, applied headlessly through
 * `applyRowSeeds` onto a fresh engine state (AC-058, the «model −0%»
 * invariant carries over). The registry arrives as a minimal projection
 * (`MemoryExperiment`) — the UI adapts `ExperimentDocument`s; unit tests run
 * on the projection.
 *
 * Every threshold in this file is OUR policy and is NAMED in the output
 * strings (cross-cutting doctrine, plan §3).
 */

import {
  buildInitialState,
  chainBreakEven,
  mechanicBreakEven,
  menuTakes,
  profitPerStart,
  requiredValuePerPressed,
  round99,
  sumTakes,
  twoProportionNPerArm,
  valuePerPressed,
  mechanicsExtras,
  pickDownsellCandidate,
  pickUpsellCandidate,
  SOLO_PRIOR_BY_FAMILY,
  type CalculatorModelState,
  type CalculatorOffer,
  type ChainKnob,
} from './pricingCalculator';
import { applyRowSeeds, rowServedPrefill, type RowSeedProduct, type RowServedSeeds } from './rowServedPrefill';
import type { FunnelTrafficRow } from './funnelTrafficSourcesRows';

// ---------------------------------------------------------------------------
// Policy knobs (OUR policy — every one of them is named in output copy)
// ---------------------------------------------------------------------------

/** D-CELL-2: campaigns by descending spend until this share of window spend. */
export const MAIN_MASS_SPEND_SHARE = 0.9;

/** AC-065 router: a rate knob under this share of the cross-cell best is a funnel problem, not a pricing one. */
export const BROKEN_RATE_RATIO = 0.5;

/** AC-065 router: cost per start above this multiple of the cross-cell best is a buying-side problem. */
export const BROKEN_CPS_RATIO = 2;

/** Price-up candidates step toward the next anchor by this factor before .99 rounding. */
export const PRICE_UP_STEP = 1.25;

/** Mechanics candidates carry this cannibalization guard (percentage points). */
export const CANNIBALIZATION_GUARD_PP = 3;

/** §2 staleness horizon: a DECIDED candidate re-proposes after this many days. */
export const DECIDED_STALE_DAYS = 180;

/** Mirror of the row-seed floor — thin-product wording matches the calculator captions. */
export const CELL_CHECKS_THIN_FLOOR = 5;

export const TRIAL_MATURITY_SLACK_DAYS = 3;

export const WTP_SCAN_UNSERVED_REASON =
  'needs a served willingness-to-pay cohort scan — not served to this surface yet';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CellZone = 'act' | 'test' | 'wait';

export interface ZoneReading {
  zone: CellZone;
  /** One sentence: the reading and its named lever/threshold/date. */
  reason: string;
  /** Present for `wait` when the maturity date is computable (ISO date). */
  date?: string;
}

export interface CellMixEntry {
  priceId: string;
  label: string;
  subs: number;
  pressed: number | null;
  checks?: number;
  /** Caption facts: cohort age + thin flag (below the named floor). */
  ageDays?: number;
  thin: boolean;
}

export interface DecisionCell {
  key: string;
  label: string;
  spend: number;
  /** Share of the summed campaign spend in this window (money is additive). */
  spendShare: number;
  /** spendExact / windowSpend when the lane served ad×day money; null otherwise. */
  spendExactShare: number | null;
  seeds: RowServedSeeds;
  /** Headless engine state after the row seeds applied (offers merged below). */
  state: CalculatorModelState;
  /** Registry offers + report-mode synthesized offers the apply step built. */
  offerPool: Record<string, CalculatorOffer>;
  model: {
    cac: number | null;
    servedCac: number | null;
    /** (model − served) / served; null when either side is absent. */
    deltaShare: number | null;
    profitPerStart: number | null;
  };
  mix: CellMixEntry[];
  /** paying − Σ matched subs when positive (AC-056 residual, rendered as its own line). */
  residualPayers: number | null;
  zone: ZoneReading | null;
  /** Typed reason when zone is null («not a cell» readings — missing lanes). */
  zoneAbsence?: string;
}

export interface RemainderPool {
  campaigns: number;
  spend: number;
  spendShare: number;
}

export type CandidateAxis =
  | 'nominal_price_up'
  | 'nominal_price_down'
  | 'trial_vs_no_trial'
  | 'decline_downsell'
  | 'attach_upsell'
  | 'wtp_cohort_menu';

export interface CandidateQuote {
  nPerArm: number;
  days: number;
  calendarSpend: number;
  lookDay: number;
}

/**
 * Structured arm identities for the 3a confirm (AC-076/077): which pool offers
 * make the control menu, which one the treatment modifies, and what the
 * variant introduces (a target price, or a mechanics mate). Absent on the
 * typed wait-state candidates (WTP) — they never reach the confirm.
 */
export interface CandidateArms {
  /** Control menu at generation: the cell's menu offer ids (offerPool keys). */
  controlOfferIds: string[];
  /** The offer the treatment modifies (lead plan / trial plan). */
  leadOfferId: string;
  /** Price axes only: the variant's target nominal price. */
  targetPrice?: number;
  /** Mechanics only: the mate offer (downsell/upsell) from the pool. */
  mateOfferId?: string;
}

export interface Candidate {
  id: string;
  key: string;
  axis: CandidateAxis;
  cellKey: string | null;
  /** Plain-language axis sentence ending with the question it answers (§6 7a d). */
  headline: string;
  sentence: string;
  /** Provenance-grammar evidence line (§6 7a e). */
  evidence: string;
  /** Readable criterion sentence; the technical metric id demoted (§6 7a f). */
  criterion: { sentence: string; metricId: string };
  zone: ZoneReading;
  quote?: CandidateQuote;
  /** Confirm prefill identities (absent on wait-typed candidates). */
  arms?: CandidateArms;
}

export type MemoryStatus = 'proposed' | 'suppressed' | 'history' | 'reproposed' | 'draft';

export interface MemoryExperiment {
  id: string;
  status: 'draft' | 'running' | 'decided' | 'stopped';
  /** Registry document name — the draft card names its draft (round-5 A-10). */
  name?: string;
  candidateKey?: string;
  axis?: string;
  alias?: string;
  decidedAt?: string;
  verdict?: string;
  effect?: string;
  stoppedNote?: string;
}

export interface CandidateWithMemory extends Candidate {
  memory: MemoryStatus;
  /** history: verdict/date/link data; suppressed: the running experiment; reproposed: prior story. */
  memoryRef?: MemoryExperiment;
  /** suppressed only: the three options are a fixed doctrine note (§6 #5). */
  suppressionOptions?: readonly ['amendment_arm', 'other_placement', 'wait'];
}

export interface RecommendationsResult {
  cells: DecisionCell[];
  remainder: RemainderPool | null;
  candidates: CandidateWithMemory[];
  /** act-zone cells surface as banner readings, not cards. */
  actReadings: Array<{ cellKey: string; label: string; reason: string }>;
  inputHash: string;
}

export interface RecommendationsInput {
  rows: FunnelTrafficRow[];
  window: { from: string; to: string };
  offers: Record<string, CalculatorOffer>;
  experiments: MemoryExperiment[];
  /** Scope alias for the running-suppression match (placement grain, §6 #5). */
  alias?: string;
}

// ---------------------------------------------------------------------------
// Canonical candidate key (§2 item 1, AC-066)
// ---------------------------------------------------------------------------

const slugPart = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'na';

/**
 * Deterministic slug of {axis, scope, offer set, grain} — the dedup identity
 * stored on every generated candidate and every registered draft. The offer
 * set is order-insensitive by construction (sorted before joining).
 */
export function canonicalCandidateKey(input: {
  axis: string;
  scope: string;
  offerIds: string[];
  grain: string;
}): string {
  const offers = [...input.offerIds].sort().map(slugPart).join('+') || 'none';
  return [slugPart(input.axis), slugPart(input.scope), slugPart(input.grain), offers].join('__');
}

// ---------------------------------------------------------------------------
// Stable input hash (golden replay discipline, AC-062) — local FNV-1a to keep
// this module free of cross-module imports.
// ---------------------------------------------------------------------------

const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const keys = Object.keys(value as Record<string, unknown>).sort();
  return `{${keys.map(key => `${JSON.stringify(key)}:${stableStringify((value as Record<string, unknown>)[key])}`).join(',')}}`;
};

export function hashRecommendationsInput(value: unknown): string {
  const input = stableStringify(value);
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

// ---------------------------------------------------------------------------
// AC-057 — main-mass selection + remainder pool
// ---------------------------------------------------------------------------

const rowSpend = (row: FunnelTrafficRow): number =>
  typeof row.spend.value === 'number' && Number.isFinite(row.spend.value) ? row.spend.value : 0;

export interface MainMassSplit {
  selected: FunnelTrafficRow[];
  remainder: RemainderPool | null;
  totalSpend: number;
}

/**
 * Campaign rows by descending spend until cumulative ≥ 90% of the summed
 * campaign spend (MAIN_MASS_SPEND_SHARE); the tail pools. Creative rows and
 * the untagged bucket never participate (D-CELL-1; «(not set)» is hygiene,
 * not a fixable campaign).
 */
export function splitMainMass(rows: FunnelTrafficRow[]): MainMassSplit {
  const campaigns = rows
    .filter(row => row.kind === 'campaign' && !row.isNoCampaign)
    .sort((a, b) => rowSpend(b) - rowSpend(a));
  const totalSpend = campaigns.reduce((sum, row) => sum + rowSpend(row), 0);
  if (totalSpend <= 0) return { selected: campaigns, remainder: null, totalSpend: 0 };
  const selected: FunnelTrafficRow[] = [];
  let cumulative = 0;
  let index = 0;
  while (index < campaigns.length && cumulative < MAIN_MASS_SPEND_SHARE * totalSpend) {
    selected.push(campaigns[index]);
    cumulative += rowSpend(campaigns[index]);
    index += 1;
  }
  const tail = campaigns.slice(index);
  const tailSpend = tail.reduce((sum, row) => sum + rowSpend(row), 0);
  return {
    selected,
    remainder: tail.length > 0
      ? { campaigns: tail.length, spend: tailSpend, spendShare: tailSpend / totalSpend }
      : null,
    totalSpend,
  };
}

// ---------------------------------------------------------------------------
// Headless cell assembly (AC-058) + zone classification (AC-059)
// ---------------------------------------------------------------------------

const KNOB_LABELS: Record<ChainKnob, string> = {
  cps: 'cost per start',
  p1: 'first → second',
  p2: 'second → paywall',
  p3: 'paywall → pressed',
};

const isoDatePlusDays = (iso: string, days: number): string => {
  const date = new Date(iso);
  date.setUTCDate(date.getUTCDate() + Math.ceil(days));
  return date.toISOString().slice(0, 10);
};

interface CellComputation {
  cell: DecisionCell;
  bestByKnob: Partial<Record<ChainKnob, number>>;
}

function assembleCell(
  row: FunnelTrafficRow,
  window: { from: string; to: string },
  offers: Record<string, CalculatorOffer>,
  spendShareOfTotal: number,
): DecisionCell | null {
  const seeds = rowServedPrefill(row, window);
  if (!seeds) return null;
  const base = buildInitialState(offers, Object.keys(offers));
  const applied = applyRowSeeds({ base, provenance: null, offers, seeds });
  const offerPool: Record<string, CalculatorOffer> = { ...offers, ...applied.offers };
  const state = applied.state;

  const chainComplete =
    seeds.cps !== undefined && !!seeds.p1 && !!seeds.p2 && !!seeds.p3;
  const takes = menuTakes(state.menu, offerPool, state.solos);
  const extras = mechanicsExtras(state, offerPool, takes);
  const totalB = sumTakes(takes) + extras.buyersAdd;
  const valuePressed = valuePerPressed(state, offerPool);
  const { chain } = state;
  const modelCac = chainComplete && totalB > 0
    ? chain.cps / (chain.p1 * chain.p2 * chain.p3 * totalB)
    : null;
  const servedCac = seeds.cac?.value ?? null;
  const profit = chainComplete ? profitPerStart(chain, valuePressed) : null;

  const pressed = seeds.p3?.numerator ?? null;
  const mix: CellMixEntry[] = (seeds.products ?? []).map(product => ({
    priceId: product.priceId,
    label:
      offerPool[`row_${product.priceId}`]?.label
      ?? Object.values(offerPool).find(offer => offer.stripePriceId === product.priceId)?.label
      ?? product.priceId,
    subs: product.subs,
    pressed,
    ...(typeof product.checks === 'number' ? { checks: product.checks } : {}),
    ...(typeof product.ageDays === 'number' ? { ageDays: product.ageDays } : {}),
    thin: product.subs < CELL_CHECKS_THIN_FLOOR,
  }));
  const matchedSubs = (seeds.products ?? []).reduce((sum, product) => sum + product.subs, 0);
  const paying = seeds.cac?.paying;
  const residualPayers =
    typeof paying === 'number' && paying > matchedSubs ? paying - matchedSubs : null;

  return {
    key: row.id,
    label: row.label,
    spend: rowSpend(row),
    spendShare: spendShareOfTotal,
    spendExactShare:
      row.spend.basis === 'ad_day' && typeof row.spend.exact === 'number' && rowSpend(row) > 0
        ? row.spend.exact / rowSpend(row)
        : null,
    seeds,
    state,
    offerPool,
    model: {
      cac: modelCac,
      servedCac,
      deltaShare: modelCac !== null && servedCac ? (modelCac - servedCac) / servedCac : null,
      profitPerStart: profit,
    },
    mix,
    residualPayers,
    zone: null,
    ...(chainComplete ? {} : { zoneAbsence: describeChainAbsence(seeds) }),
  };
}

function describeChainAbsence(seeds: RowServedSeeds): string {
  const missing = (['cps', 'p1', 'p2', 'p3'] as const).filter(knob =>
    knob === 'cps' ? seeds.cps === undefined : !seeds[knob],
  );
  const reasons = missing.map(knob => seeds.absences[knob] ?? 'n/a').join('; ');
  return `not a cell — ${missing.map(k => KNOB_LABELS[k]).join(', ')} not seedable (${reasons})`;
}

/** Immature trial cohort on the cell → the wait date (max across products). */
function trialWait(seeds: RowServedSeeds, window: { from: string; to: string }): { date: string; product: RowSeedProduct } | null {
  let latest: { date: string; product: RowSeedProduct; daysLeft: number } | null = null;
  for (const product of seeds.products ?? []) {
    const trialDays = product.trialDays ?? 0;
    if (trialDays <= 0 || (product.trialStarted ?? 0) <= 0) continue;
    if ((product.trialConverted ?? 0) >= 1) continue; // matured enough to seed
    const age = product.ageDays ?? 0;
    const daysLeft = trialDays + TRIAL_MATURITY_SLACK_DAYS - age;
    if (daysLeft <= 0) continue;
    if (!latest || daysLeft > latest.daysLeft) {
      latest = { date: isoDatePlusDays(window.to, daysLeft), product, daysLeft };
    }
  }
  return latest ? { date: latest.date, product: latest.product } : null;
}

const pct = (value: number): string => `${(value * 100).toFixed(1)}%`;

/**
 * AC-059 decision table. `bests` = cross-cell best observed per knob (the
 * realism anchor — «campaign best», OUR policy). Order: wait → act → test.
 */
export function classifyZone(
  cell: DecisionCell,
  bests: Partial<Record<ChainKnob, number>>,
  window: { from: string; to: string },
): ZoneReading | null {
  if (cell.zoneAbsence) return null;
  const { state, offerPool, seeds } = cell;
  const valuePressed = valuePerPressed(state, offerPool);
  const profit = cell.model.profitPerStart ?? 0;

  const wait = trialWait(seeds, window);
  if (wait) {
    return {
      zone: 'wait',
      reason: `trial cohort decisive ~${wait.date} (trial length + ${TRIAL_MATURITY_SLACK_DAYS}-day settlement slack — our policy)`,
      date: wait.date,
    };
  }
  if (cell.mix.length > 0 && cell.mix.every(entry => entry.thin)) {
    return {
      zone: 'wait',
      reason: `all products below the ${CELL_CHECKS_THIN_FLOOR}-subscription floor (our policy) — directional only`,
    };
  }

  if (profit >= 0) {
    return { zone: 'test', reason: 'profitable at current values — price upside is testable' };
  }

  // profit < 0: is any single feasible knob move enough? Feasible = within the
  // cross-cell best observed (the realism anchor).
  const feasible: Array<{ knob: ChainKnob; required: number; best: number }> = [];
  const infeasible: Array<{ knob: ChainKnob; required: number; best: number | undefined }> = [];
  (['p1', 'p2', 'p3'] as const).forEach(knob => {
    const required = chainBreakEven(state.chain, valuePressed, knob);
    const best = bests[knob];
    if (typeof best === 'number' && required <= best) feasible.push({ knob, required, best });
    else infeasible.push({ knob, required, best });
  });
  const requiredCps = chainBreakEven(state.chain, valuePressed, 'cps');
  const bestCps = bests.cps;
  if (typeof bestCps === 'number' && requiredCps >= bestCps) {
    feasible.push({ knob: 'cps', required: requiredCps, best: bestCps });
  } else {
    infeasible.push({ knob: 'cps', required: requiredCps, best: bestCps });
  }

  if (feasible.length === 0) {
    const worst = infeasible[0];
    const bestLabel = typeof worst?.best === 'number'
      ? (worst.knob === 'cps' ? `$${worst.best.toFixed(2)}` : pct(worst.best))
      : 'n/d';
    const requiredLabel = worst
      ? (worst.knob === 'cps' ? `$${worst.required.toFixed(2)}` : pct(worst.required))
      : 'n/d';
    return {
      zone: 'act',
      reason: `no path at any knob — even ${worst ? KNOB_LABELS[worst.knob] : 'the best gate'} would need ${requiredLabel} against best observed ${bestLabel} (realism anchor: campaign best, our policy)`,
    };
  }
  const lever = feasible[0];
  const requiredLabel = lever.knob === 'cps' ? `≤ $${lever.required.toFixed(2)}` : `≥ ${pct(lever.required)}`;
  const bestLabel = lever.knob === 'cps' ? `$${lever.best.toFixed(2)}` : pct(lever.best);
  return {
    zone: 'test',
    reason: `needs ${KNOB_LABELS[lever.knob]} ${requiredLabel} · campaign best ${bestLabel}`,
  };
}

// ---------------------------------------------------------------------------
// AC-062/063/065 — candidate generation
// ---------------------------------------------------------------------------

const windowDays = (window: { from: string; to: string }): number =>
  Math.max(1, (new Date(window.to).getTime() - new Date(window.from).getTime()) / 86_400_000);

function quoteForTakes(
  cell: DecisionCell,
  window: { from: string; to: string },
  baselineTake: number,
  targetTake: number,
): CandidateQuote | undefined {
  const seeds = cell.seeds;
  if (!seeds.p1 || !seeds.p3 || seeds.cps === undefined) return undefined;
  if (Math.abs(baselineTake - targetTake) < 0.005) return undefined;
  const days = windowDays(window);
  const startsPerDay = seeds.p1.denominator / days;
  const { chain } = cell.state;
  const pressedPerDay = startsPerDay * chain.p1 * chain.p2 * chain.p3;
  if (pressedPerDay <= 0) return undefined;
  const nPerArm = Math.ceil(twoProportionNPerArm(baselineTake, targetTake));
  const fillDays = nPerArm / (pressedPerDay / 2);
  return {
    nPerArm,
    days: Math.ceil(fillDays),
    calendarSpend: fillDays * startsPerDay * seeds.cps,
    lookDay: Math.ceil(fillDays),
  };
}

/** The cell's leading subscription offer (largest calibrated take). */
function leadingOffer(cell: DecisionCell): { offer: CalculatorOffer; take: number } | null {
  const takes = menuTakes(cell.state.menu, cell.offerPool, cell.state.solos);
  let best: { offer: CalculatorOffer; take: number } | null = null;
  for (const id of cell.state.menu) {
    const offer = cell.offerPool[id];
    if (!offer || offer.price === null || !offer.isSubscription) continue;
    if (offer.id === 'row_residual') continue;
    const take = takes[id] ?? 0;
    if (!best || take > best.take) best = { offer, take };
  }
  return best;
}

/** AC-065: the E3+ cost-ladder router — which side owns the fix. */
export function routePricingEligibility(
  cell: DecisionCell,
  bests: Partial<Record<ChainKnob, number>>,
): { eligible: boolean; reason?: string } {
  const { chain } = cell.state;
  for (const knob of ['p1', 'p2'] as const) {
    const best = bests[knob];
    if (typeof best === 'number' && best > 0 && chain[knob] < BROKEN_RATE_RATIO * best) {
      return {
        eligible: false,
        reason: `${KNOB_LABELS[knob]} ${pct(chain[knob])} is under ${Math.round(BROKEN_RATE_RATIO * 100)}% of campaign best ${pct(best)} — the funnel fix owns this cell (our policy)`,
      };
    }
  }
  const bestCps = bests.cps;
  if (typeof bestCps === 'number' && bestCps > 0 && chain.cps > BROKEN_CPS_RATIO * bestCps) {
    return {
      eligible: false,
      reason: `cost per start $${chain.cps.toFixed(2)} is over ${BROKEN_CPS_RATIO}× campaign best $${bestCps.toFixed(2)} — the buying side owns this cell (our policy)`,
    };
  }
  return { eligible: true };
}

const money = (value: number): string => `$${value.toFixed(2)}`;

function priceCandidate(
  cell: DecisionCell,
  window: { from: string; to: string },
  direction: 'up' | 'down',
  scope: string,
): Candidate | null {
  const lead = leadingOffer(cell);
  if (!lead || lead.offer.price === null || lead.take <= 0) return null;
  const price = lead.offer.price;
  const target = direction === 'up'
    ? round99(price * PRICE_UP_STEP)
    : (() => {
        const state = cell.state;
        const value = valuePerPressed(state, cell.offerPool);
        const required = requiredValuePerPressed(state.chain);
        if (value <= 0 || required >= value) return null;
        return round99(price * (required / value));
      })();
  if (target === null || !Number.isFinite(target) || target === price || target <= 0) return null;
  if (direction === 'down' && target >= price) return null;
  // Value-neutral retention threshold (our policy): the move pays back while
  // take × price holds — required take = current × (price / target).
  const requiredTake = Math.min(0.95, lead.take * (price / target));
  const axis: CandidateAxis = direction === 'up' ? 'nominal_price_up' : 'nominal_price_down';
  const key = canonicalCandidateKey({ axis, scope, offerIds: [lead.offer.id], grain: 'campaign' });
  const pressed = cell.seeds.p3?.numerator ?? 0;
  const observedSubs = Math.round(lead.take * pressed);
  return {
    id: `${cell.key}:${axis}`,
    key,
    axis,
    cellKey: cell.key,
    headline: `${lead.offer.label.replace(/ ?\$[\d.,]+.*$/, '')} ${money(price)} → ${money(target)}`,
    sentence: `Same menu, different price tag on the leading plan. Answers: is ${money(target)} better than ${money(price)}?`,
    evidence: `row: observed take ${pct(lead.take)} · ${observedSubs.toLocaleString('en-US')}/${pressed.toLocaleString('en-US')} pressed`,
    criterion: {
      sentence: `The variant wins if, at the pre-declared look, take holds ≥ ${pct(requiredTake)} (value-neutral retention threshold — our policy) with probability ≥ 95%.`,
      metricId: 'contribution_per_assigned_eligible_user',
    },
    zone: { zone: 'test', reason: `pays back while take stays ≥ ${pct(requiredTake)}` },
    ...(quoteForTakes(cell, window, lead.take, requiredTake) ? { quote: quoteForTakes(cell, window, lead.take, requiredTake) } : {}),
    arms: { controlOfferIds: [...cell.state.menu], leadOfferId: lead.offer.id, targetPrice: target },
  };
}

function trialCandidate(cell: DecisionCell, scope: string): Candidate | null {
  const trialOffer = cell.state.menu
    .map(id => cell.offerPool[id])
    .find(offer => offer && (offer.trialDays ?? 0) > 0 && offer.price !== null);
  if (!trialOffer) return null;
  const conv = cell.state.trialConv[trialOffer.id];
  if (typeof conv !== 'number') return null; // seeds carry conv only on matured ≥1-conversion cohorts
  const soloTrial = cell.state.solos[trialOffer.id] ?? 0;
  if (soloTrial <= 0) return null;
  // Break-even trial→paid = the press-rate a no-trial variant of the same
  // plan would keep (benchmark prior when unobserved) ÷ the trial press-rate.
  const soloNoTrial = SOLO_PRIOR_BY_FAMILY[trialOffer.family];
  const convBe = Math.min(0.99, soloNoTrial / soloTrial);
  const axis: CandidateAxis = 'trial_vs_no_trial';
  return {
    id: `${cell.key}:${axis}`,
    key: canonicalCandidateKey({ axis, scope, offerIds: [trialOffer.id], grain: 'campaign' }),
    axis,
    cellKey: cell.key,
    headline: `${trialOffer.label.replace(' · row', '')}: trial vs no trial`,
    sentence: 'Same plan, with and without the free trial. Answers: does the trial convert enough to beat charging up front?',
    evidence: `row: observed trial→paid ${pct(conv)} · press-rate ${pct(soloTrial)} (no-trial press benchmark ${pct(soloNoTrial)} — our editorial prior)`,
    criterion: {
      sentence: `Keep the trial if matured trial→paid holds ≥ ${pct(convBe)} (press-parity break-even — our policy) at the pre-declared look.`,
      metricId: 'contribution_per_assigned_eligible_user',
    },
    zone: { zone: 'test', reason: `break-even trial→paid ${pct(convBe)}` },
    arms: { controlOfferIds: [...cell.state.menu], leadOfferId: trialOffer.id },
  };
}

function mechanicCandidate(
  cell: DecisionCell,
  kind: 'downsell' | 'upsell',
  scope: string,
): Candidate | null {
  const lead = leadingOffer(cell);
  if (!lead) return null;
  // Synthesized (row-built) offers carry no mechanics config on the state —
  // pick the natural mate straight from the pool (same engine pickers).
  const config = cell.state.mechanics.perOffer[lead.offer.id]?.[kind];
  const pool = Object.fromEntries(
    Object.entries(cell.offerPool).filter(([id]) => id !== 'row_residual'),
  );
  const mateId = config?.offerId
    ?? (kind === 'downsell'
      ? pickDownsellCandidate(lead.offer.id, pool)
      : pickUpsellCandidate(lead.offer.id, pool));
  const mate = mateId ? cell.offerPool[mateId] : null;
  if (!mate || mate.price === null || mate.id === lead.offer.id) return null;
  // Content guard: a decline downsell must actually be cheaper than the
  // declined plan, an attach upsell more expensive — the engine's pickers
  // fall back to «any other offer», which is fine for defaults but wrong as
  // a proposed experiment.
  if (lead.offer.price !== null) {
    if (kind === 'downsell' && mate.price >= lead.offer.price) return null;
    if (kind === 'upsell' && mate.price <= lead.offer.price) return null;
  }
  const existing = cell.state.mechanics.perOffer[lead.offer.id]
    ?? { downsell: { enabled: false, offerId: null, conv: 0 }, upsell: { enabled: false, offerId: null, conv: 0 } };
  const stateWithMate: CalculatorModelState = {
    ...cell.state,
    mechanics: {
      ...cell.state.mechanics,
      perOffer: {
        ...cell.state.mechanics.perOffer,
        [lead.offer.id]: { ...existing, [kind]: { ...existing[kind], offerId: mate.id } },
      },
    },
  };
  const be = mechanicBreakEven(stateWithMate, cell.offerPool, lead.offer.id, kind);
  if (be === null) return null;
  const takes = menuTakes(cell.state.menu, cell.offerPool, cell.state.solos);
  const total = sumTakes(takes);
  const axis: CandidateAxis = kind === 'downsell' ? 'decline_downsell' : 'attach_upsell';
  const pressed = cell.seeds.p3?.numerator ?? 0;
  const decliners = Math.round((1 - total) * pressed);
  const requiredConv = Math.max(be, 0);
  const upside = be <= 0;
  return {
    id: `${cell.key}:${axis}`,
    key: canonicalCandidateKey({ axis, scope, offerIds: [lead.offer.id, mate.id], grain: 'campaign' }),
    axis,
    cellKey: cell.key,
    headline: kind === 'downsell'
      ? `Decline downsell: ${mate.label.replace(' · row', '')}`
      : `Attach upsell: ${mate.label.replace(' · row', '')}`,
    sentence: kind === 'downsell'
      ? 'People who press buy and decline see one cheaper offer. Answers: does a second-chance price recover decliners without eating full-price sales?'
      : 'Buyers get one add-on offer at checkout. Answers: does the attach lift value per buyer without hurting completion?',
    evidence: kind === 'downsell'
      ? `row: observed ${decliners.toLocaleString('en-US')}/${pressed.toLocaleString('en-US')} pressed decline everything · leading take ${pct(lead.take)} to protect`
      : `row: observed leading take ${pct(lead.take)} · ${pressed.toLocaleString('en-US')} pressed`,
    criterion: {
      sentence: upside
        ? `The chain is already profitable — the variant wins if acceptance clears ${pct(Math.max(requiredConv, 0.01))} and cannibalization stays under ${CANNIBALIZATION_GUARD_PP} pp (guard — our policy).`
        : `The variant wins if acceptance clears ${pct(requiredConv)} (chain break-even) with cannibalization under ${CANNIBALIZATION_GUARD_PP} pp (guard — our policy).`,
      metricId: 'contribution_per_assigned_eligible_user',
    },
    zone: { zone: 'test', reason: `break-even acceptance ${pct(Math.max(requiredConv, 0.01))}` },
    arms: { controlOfferIds: [...cell.state.menu], leadOfferId: lead.offer.id, mateOfferId: mate.id },
  };
}

function wtpCandidate(scope: string): Candidate {
  const axis: CandidateAxis = 'wtp_cohort_menu';
  return {
    id: `project:${axis}`,
    key: canonicalCandidateKey({ axis, scope, offerIds: [], grain: 'project' }),
    axis,
    cellKey: null,
    headline: 'Premium menu for the high-willingness cohort',
    sentence: 'Show a premium-first menu only to the answer cohort that historically buys at higher prices. Answers: does the top willingness bucket carry a higher anchor?',
    evidence: WTP_SCAN_UNSERVED_REASON,
    criterion: {
      sentence: 'Assembles when the willingness-to-pay cohort scan is served to this surface — no measured claim before that.',
      metricId: 'contribution_per_assigned_eligible_user',
    },
    zone: { zone: 'wait', reason: WTP_SCAN_UNSERVED_REASON },
  };
}

// ---------------------------------------------------------------------------
// §2 experiment memory (AC-067)
// ---------------------------------------------------------------------------

const SUPPRESSION_OPTIONS = ['amendment_arm', 'other_placement', 'wait'] as const;

export function applyMemory(
  candidates: Candidate[],
  experiments: MemoryExperiment[],
  options?: { alias?: string; nowIso?: string },
): CandidateWithMemory[] {
  const now = options?.nowIso ? new Date(options.nowIso).getTime() : Date.now();
  return candidates.map(candidate => {
    const running = experiments.find(experiment =>
      experiment.status === 'running'
      && (experiment.candidateKey === candidate.key
        || (!!experiment.axis && experiment.axis === candidate.axis
          && !!options?.alias && experiment.alias === options.alias)),
    );
    if (running) {
      return { ...candidate, memory: 'suppressed', memoryRef: running, suppressionOptions: SUPPRESSION_OPTIONS };
    }
    // AC-082: a registered draft with the SAME key removes the candidate from
    // ranked proposals — it renders as the «draft registered» card. Key-exact
    // only: a draft never suppresses by axis+alias (that is the running rule).
    const draft = experiments.find(experiment =>
      experiment.status === 'draft' && experiment.candidateKey === candidate.key,
    );
    if (draft) {
      return { ...candidate, memory: 'draft', memoryRef: draft };
    }
    const decided = experiments.find(experiment =>
      experiment.status === 'decided' && experiment.candidateKey === candidate.key,
    );
    if (decided) {
      const decidedAt = decided.decidedAt ? new Date(decided.decidedAt).getTime() : now;
      const stale = now - decidedAt > DECIDED_STALE_DAYS * 86_400_000;
      if (!stale) return { ...candidate, memory: 'history', memoryRef: decided };
      // Named policy trigger: the staleness horizon elapsed (DECIDED_STALE_DAYS).
      return { ...candidate, memory: 'reproposed', memoryRef: decided };
    }
    const stopped = experiments.find(experiment =>
      experiment.status === 'stopped' && experiment.candidateKey === candidate.key,
    );
    if (stopped) return { ...candidate, memory: 'reproposed', memoryRef: stopped };
    return { ...candidate, memory: 'proposed' };
  });
}

// ---------------------------------------------------------------------------
// Assembly + §6 #6 memoization (AC-061)
// ---------------------------------------------------------------------------

/** Cross-cell best observed per knob — the realism anchor (zones + router). */
function collectBests(cells: DecisionCell[]): Partial<Record<ChainKnob, number>> {
  const bests: Partial<Record<ChainKnob, number>> = {};
  for (const cell of cells) {
    if (cell.zoneAbsence) continue;
    const { chain } = cell.state;
    (['p1', 'p2', 'p3'] as const).forEach(knob => {
      if (bests[knob] === undefined || chain[knob] > (bests[knob] as number)) bests[knob] = chain[knob];
    });
    if (bests.cps === undefined || chain.cps < bests.cps) bests.cps = chain.cps;
  }
  return bests;
}

/**
 * S13 verdict chips (AC-069/072): zone readings for EVERY campaign row —
 * unlike the cells table this ignores the main-mass floor (a chip is a
 * reading about the row, not a ranked cell). null = «not a cell» (missing
 * lanes) → the row renders NO chip (absence of evidence is not a gray alarm).
 */
export function buildRowZones(
  rows: FunnelTrafficRow[],
  window: { from: string; to: string },
  offers: Record<string, CalculatorOffer> = {},
): Map<string, ZoneReading | null> {
  const zones = new Map<string, ZoneReading | null>();
  const cells: DecisionCell[] = [];
  for (const row of rows) {
    if (row.kind !== 'campaign' || row.isNoCampaign) continue;
    const cell = assembleCell(row, window, offers, 0);
    if (cell) cells.push(cell); else zones.set(row.id, null);
  }
  const bests = collectBests(cells);
  for (const cell of cells) {
    zones.set(cell.key, classifyZone(cell, bests, window));
  }
  return zones;
}

export function assembleRecommendations(input: RecommendationsInput): RecommendationsResult {
  const scope = input.alias ?? 'project';
  const split = splitMainMass(input.rows);
  const cells: DecisionCell[] = [];
  for (const row of split.selected) {
    const cell = assembleCell(
      row, input.window, input.offers,
      split.totalSpend > 0 ? rowSpend(row) / split.totalSpend : 0,
    );
    if (cell) cells.push(cell);
  }

  const bests = collectBests(cells);

  const actReadings: RecommendationsResult['actReadings'] = [];
  for (const cell of cells) {
    cell.zone = classifyZone(cell, bests, input.window);
    if (cell.zone?.zone === 'act') {
      actReadings.push({ cellKey: cell.key, label: cell.label, reason: cell.zone.reason });
    }
  }

  const candidates: Candidate[] = [];
  for (const cell of cells) {
    if (!cell.zone || cell.zone.zone === 'act') continue;
    const route = routePricingEligibility(cell, bests);
    if (!route.eligible) continue; // AC-065: the funnel/buying side owns it
    if (cell.zone.zone === 'wait') continue; // waits render on the cell, not as cards
    const up = priceCandidate(cell, input.window, 'up', scope);
    if (up) candidates.push(up);
    const down = priceCandidate(cell, input.window, 'down', scope);
    if (down) candidates.push(down);
    const trial = trialCandidate(cell, scope);
    if (trial) candidates.push(trial);
    const downsell = mechanicCandidate(cell, 'downsell', scope);
    if (downsell) candidates.push(downsell);
    const upsell = mechanicCandidate(cell, 'upsell', scope);
    if (upsell) candidates.push(upsell);
  }
  candidates.push(wtpCandidate(scope));

  // Deterministic order: cell spend desc, then a fixed axis order (AC-062).
  const axisOrder: CandidateAxis[] = [
    'nominal_price_up', 'nominal_price_down', 'trial_vs_no_trial',
    'decline_downsell', 'attach_upsell', 'wtp_cohort_menu',
  ];
  const spendByCell = new Map(cells.map(cell => [cell.key, cell.spend]));
  candidates.sort((a, b) => {
    const spendDelta = (spendByCell.get(b.cellKey ?? '') ?? -1) - (spendByCell.get(a.cellKey ?? '') ?? -1);
    if (spendDelta !== 0) return spendDelta;
    return axisOrder.indexOf(a.axis) - axisOrder.indexOf(b.axis);
  });

  const withMemory = applyMemory(candidates, input.experiments, {
    ...(input.alias ? { alias: input.alias } : {}),
    nowIso: input.window.to,
  });

  return {
    cells,
    remainder: split.remainder,
    candidates: withMemory,
    actReadings,
    inputHash: hashRecommendationsInput({
      window: input.window,
      rows: input.rows.map(row => ({ id: row.id, spend: rowSpend(row) })),
      offers: Object.keys(input.offers).sort(),
      experiments: input.experiments.map(e => `${e.id}:${e.status}`),
    }),
  };
}

/**
 * §6 #6 on-open cache: memoize per {window, scope, rows identity, registry
 * identity}. Reference equality on the arrays is the payload identity — a
 * refetch produces new arrays and invalidates naturally (AC-061).
 */
export function createRecommendationsCache(
  compute: (input: RecommendationsInput) => RecommendationsResult = assembleRecommendations,
): (input: RecommendationsInput) => RecommendationsResult {
  let lastKey: { rows: unknown; experiments: unknown; offers: unknown; window: string } | null = null;
  let lastResult: RecommendationsResult | null = null;
  return input => {
    const windowKey = `${input.window.from}|${input.window.to}|${input.alias ?? ''}`;
    if (
      lastResult
      && lastKey
      && lastKey.rows === input.rows
      && lastKey.experiments === input.experiments
      && lastKey.offers === input.offers
      && lastKey.window === windowKey
    ) {
      return lastResult;
    }
    lastResult = compute(input);
    lastKey = { rows: input.rows, experiments: input.experiments, offers: input.offers, window: windowKey };
    return lastResult;
  };
}
